import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { getCatalogCategories, getCatalogProducts } from '@/api/directSale';
import type { Product } from '@/api/products';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { useIsPhone } from './useIsPhone';

/** Colour swatches beyond this collapse into a "+n" hint. */
const MAX_VISIBLE_COLORS = 6;
const MAX_VISIBLE_COLORS_PHONE = 4;

/**
 * Cards rendered at once. The catalogue's "thumbnails" are still the
 * 1200 px originals (up to 600 KB each); 135 of them at once took minutes
 * on a phone and looked like nothing loads. `loading="lazy"` does not help
 * enough — browsers start images more than a screen ahead — so the grid
 * grows as the customer scrolls.
 */
const PAGE_SIZE_PHONE = 12;
const PAGE_SIZE = 24;

function formatPrice(value: number | undefined, language: string): string {
  if (value === undefined) return '';
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

interface ProductCardProps {
  product: Product;
  isSelected: boolean;
  selectedLabel: string;
  onSelect: (product: Product) => void;
}

function ProductCard({
  product,
  isSelected,
  selectedLabel,
  onSelect,
}: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const colors = product.colors ?? [];
  const visibleColors = colors.slice(0, MAX_VISIBLE_COLORS);
  const hiddenCount = colors.length - visibleColors.length;
  // The catalogue stores storage keys, not URLs — an unresolved key would be
  // requested from the app's own origin.
  const imageUrl = proxyStorageUrl(
    product.thumbnailImageUrl || product.mockupImageUrl
  );

  return (
    <button
      type="button"
      onClick={() => onSelect(product)}
      aria-pressed={isSelected}
      className={cn(
        'group relative block overflow-hidden rounded-section bg-paper-2 text-left transition-all hover:-translate-y-0.5',
        isSelected
          ? 'border-2 border-primary ring-1 ring-line shadow-primary-500/15'
          : 'border border-line hover:border-primary'
      )}
    >
      {isSelected && (
        <span className="absolute left-3 top-3 z-10 rounded-chip bg-primary px-2.5 py-0.5 text-[11px] font-bold text-primary-fg">
          {selectedLabel}
        </span>
      )}

      <div className="flex aspect-[1/0.9] items-center justify-center bg-gradient-to-b from-paper-2 to-white">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-[82%] w-[68%] object-contain"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="h-[82%] w-[68%] rounded-control bg-paper-2" />
        )}
      </div>

      {/* Narrow cards (two columns on a phone): price above the name, no
          material line — side by side they would squeeze the name to a word
          per line. */}
      <div className="px-3 pb-3 pt-2.5 min-[700px]:px-4 min-[700px]:pb-4 min-[700px]:pt-3.5">
        <div className="flex flex-col-reverse gap-0.5 min-[700px]:flex-row min-[700px]:items-baseline min-[700px]:justify-between min-[700px]:gap-2">
          <span className="text-sm font-bold leading-snug text-ink min-[700px]:text-[15px]">
            {product.name}
          </span>
          {product.recommendedSalesPrice !== undefined && (
            <span className="whitespace-nowrap text-sm font-bold text-primary">
              {t('directSale.product.priceFrom', {
                price: formatPrice(
                  product.recommendedSalesPrice,
                  i18n.language
                ),
              })}
            </span>
          )}
        </div>

        {product.material && (
          <div className="mt-0.5 text-xs text-mute max-[699px]:hidden">
            {product.material}
          </div>
        )}

        {colors.length > 0 && (
          // One row, always: a lone dot wrapped under the others reads as a
          // mistake. A phone card is half as wide, so it shows fewer dots
          // and counts the rest — CSS picks, the canvas-free catalogue may.
          <div className="mt-2.5 flex items-center gap-1.5 overflow-hidden">
            {visibleColors.map((color, index) => (
              <span
                key={color.id}
                title={color.name}
                style={{ backgroundColor: color.hexCode || '#e5e7eb' }}
                className={cn(
                  'h-5 w-5 flex-none rounded-full border border-line',
                  index >= MAX_VISIBLE_COLORS_PHONE && 'max-[980px]:hidden'
                )}
              />
            ))}
            {colors.length > MAX_VISIBLE_COLORS_PHONE && (
              <span className="flex-none text-[11px] text-mute min-[981px]:hidden">
                {t('directSale.product.moreColors', {
                  count: colors.length - MAX_VISIBLE_COLORS_PHONE,
                })}
              </span>
            )}
            {hiddenCount > 0 && (
              <span className="flex-none text-[11px] text-mute max-[980px]:hidden">
                {t('directSale.product.moreColors', { count: hiddenCount })}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * The Direct Sale catalogue: search, category chips and the product grid.
 * Step 1 shows it as a page; on the phone the design step opens it as a
 * sheet to switch products without leaving the editor.
 */
export function DirectSaleCatalog({
  selectedProductId,
  selectedLabel,
  onSelect,
  onCount,
}: {
  selectedProductId: string | null;
  /** Badge on the selected card ("Ausgewählt" in step 1, "Aktuell" in the sheet). */
  selectedLabel: string;
  onSelect: (product: Product) => void;
  /** Reports how many products the current filter leaves. */
  onCount?: (count: number) => void;
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const categories = useQuery({
    queryKey: ['direct-sale', 'categories'],
    queryFn: getCatalogCategories,
  });

  const products = useQuery({
    queryKey: ['direct-sale', 'products', categoryId],
    queryFn: () => getCatalogProducts(categoryId ?? undefined),
  });

  // The catalogue is a few hundred products and already loaded — filtering
  // here answers every keystroke without another request.
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const list = products.data ?? [];
    return needle === ''
      ? list
      : list.filter((p) => p.name.toLocaleLowerCase().includes(needle));
  }, [products.data, query]);

  useEffect(() => {
    if (products.isSuccess) onCount?.(visible.length);
  }, [products.isSuccess, visible.length, onCount]);

  // How many of the filtered products are on the page; a new filter starts
  // over. Grows when the sentinel below the grid comes into view — or, where
  // there is no IntersectionObserver, through the button.
  const pageSize = isPhone ? PAGE_SIZE_PHONE : PAGE_SIZE;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const [shown, setShown] = useState(pageSize);
  // A new filter starts over; the page size is read from the ref so that a
  // resize or rotation across the phone breakpoint does not count as one.
  useEffect(() => {
    setShown(pageSizeRef.current);
  }, [visible]);
  // Crossing the breakpoint only widens what is on the page, never shrinks
  // it: the customer's scroll position must survive.
  useEffect(() => {
    setShown((count) => Math.max(count, pageSize));
  }, [pageSize]);
  const hasMore = shown < visible.length;
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown((count) => count + pageSize);
        }
      },
      // Start the next page a screen before the customer gets there.
      { rootMargin: '100% 0px' }
    );
    // Re-armed after every page, so a sentinel that stays in view keeps
    // reporting.
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, pageSize, shown]);

  // Two columns on a phone, more as the width allows.
  const grid =
    'grid grid-cols-[repeat(auto-fill,minmax(min(160px,100%),1fr))] gap-x-3 gap-y-4 min-[700px]:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] min-[700px]:gap-4';

  return (
    <div>
      <label className="flex h-[50px] items-center gap-2.5 rounded-[12px] border border-line bg-white px-3.5 text-ink-3 focus-within:border-primary">
        <Search className="h-5 w-5 flex-none" />
        <input
          type="search"
          value={query}
          aria-label={t('directSale.mobile.searchProducts')}
          placeholder={t('directSale.mobile.searchProducts')}
          onChange={(e) => setQuery(e.target.value)}
          className="h-11 min-w-0 flex-1 bg-transparent text-base text-ink outline-none"
        />
      </label>

      {(categories.data?.length ?? 0) > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {categories.data?.map((category) => (
            <button
              key={category.id}
              type="button"
              aria-pressed={categoryId === category.id}
              onClick={() =>
                setCategoryId((current) =>
                  current === category.id ? null : category.id
                )
              }
              className={cn(
                'h-11 flex-none rounded-full px-4 text-sm transition-colors',
                categoryId === category.id
                  ? 'bg-ink font-semibold text-white'
                  : 'border border-line bg-paper font-medium text-ink-2 hover:border-primary hover:text-primary'
              )}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      {products.isPending && (
        <div className={cn('mt-5', grid)}>
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="aspect-[1/1.25] animate-pulse rounded-section bg-paper-2"
            />
          ))}
        </div>
      )}

      {products.isError && (
        <div className="mt-8 rounded-card border border-line bg-paper-2 p-6 text-center">
          <p className="text-sm text-ink-2">
            {t('directSale.product.loadFailed')}
          </p>
          <Button className="mt-3" onClick={() => products.refetch()}>
            {t('directSale.product.retry')}
          </Button>
        </div>
      )}

      {products.isSuccess && visible.length === 0 && (
        <p className="mt-8 text-center text-sm text-mute">
          {query.trim() === ''
            ? t('directSale.product.empty')
            : t('directSale.mobile.noMatch')}
        </p>
      )}

      {products.isSuccess && visible.length > 0 && (
        <>
          <div className={cn('mt-5', grid)}>
            {visible.slice(0, shown).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isSelected={product.id === selectedProductId}
                selectedLabel={selectedLabel}
                onSelect={onSelect}
              />
            ))}
          </div>
          {hasMore && (
            <div ref={sentinelRef} className="mt-5 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => setShown((count) => count + pageSize)}
              >
                {t('directSale.product.loadMore', {
                  count: visible.length - shown,
                })}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
